import React from 'react';
import { Profile } from '@/types/auth';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Grid, Image as ImageIcon } from 'lucide-react';

interface GalleryWidgetProps {
  profile: Profile;
}

export const GalleryWidget: React.FC<GalleryWidgetProps> = ({ profile }) => {
  const { data: bulletins, isLoading } = useQuery({
    queryKey: ['profile-gallery', profile.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bulletins')
        .select('id, image_url, thumbnail_url, content, created_at')
        .eq('user_id', profile.id)
        .not('image_url', 'is', null)
        .order('created_at', { ascending: false })
        .limit(6);

      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!bulletins || bulletins.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <div className="text-center">
          <Grid className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No posts yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full">
      <h3 className="text-sm font-medium text-muted-foreground mb-4">Recent Posts</h3>
      
      <div className="grid grid-cols-3 gap-2 h-[calc(100%-2rem)]">
        {bulletins.slice(0, 6).map((bulletin) => (
          <div
            key={bulletin.id}
            className="relative aspect-square rounded-lg overflow-hidden bg-muted group cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all"
          >
            {bulletin.thumbnail_url || bulletin.image_url ? (
              <img
                src={bulletin.thumbnail_url || bulletin.image_url}
                alt="Post"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-muted">
                <ImageIcon className="h-6 w-6 text-muted-foreground" />
              </div>
            )}
            
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200" />
          </div>
        ))}
      </div>
    </div>
  );
};